import { useState, useCallback } from 'react'
import {
  generateLocationReference,
  getTaskStatus
} from '../../../services/api/aiVideoGeneration'

const POLL_INTERVAL = 15000

/**
 * Custom hook to manage location reference generation and polling
 * Handles location map generation, editing, and batch updates
 */
export const useLocationReferences = ({ formData, setFormData } = {}) => {
  const [
    isGeneratingLocationReference,
    setIsGeneratingLocationReference
  ] = useState(false)
  const [locationGenerationProgress, setLocationGenerationProgress] = useState(
    0
  )
  const [locationGenerationStatus, setLocationGenerationStatus] = useState('')

  /**
   * Poll location task status until completion
   */
  const pollLocationTaskStatus = useCallback(
    async taskId => {
      while (true) {
        try {
          const response = await getTaskStatus(taskId)

          if (
            response.data &&
            response.data.status === 'success' &&
            response.data.output
          ) {
            setFormData(prev => ({
              ...prev,
              locationReference: response.data.output
            }))
            setIsGeneratingLocationReference(false)
            break
          } else if (response.data && response.data.status === 'failed') {
            console.error('Location reference task failed:', response.data)
            setIsGeneratingLocationReference(false)
            break
          } else {
            setLocationGenerationProgress(
              response.data && response.data.progress
                ? response.data.progress
                : locationGenerationProgress
            )
            setLocationGenerationStatus(
              response.data && response.data.status ? response.data.status : ''
            )
            await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
          }
        } catch (error) {
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
        }
      }
    },
    [setFormData, locationGenerationProgress]
  )

  /**
   * Poll add/edit location task status
   */
  const pollAddLocationTaskStatus = useCallback(
    async (taskId, newLocationMap) => {
      while (true) {
        try {
          const response = await getTaskStatus(taskId)

          if (
            response.data &&
            response.data.status === 'success' &&
            response.data.output
          ) {
            setFormData(prev => ({
              ...prev,
              locationReference: {
                ...(prev.locationReference || {}),
                ...response.data.output
              },
              locationMap: {
                ...(prev.locationMap || {}),
                ...newLocationMap
              }
            }))
            setIsGeneratingLocationReference(false)
            break
          } else if (response.data && response.data.status === 'failed') {
            console.error('Add location reference task failed:', response.data)
            setIsGeneratingLocationReference(false)
            break
          } else {
            setLocationGenerationProgress(
              response.data && response.data.progress
                ? response.data.progress
                : locationGenerationProgress
            )
            setLocationGenerationStatus(
              response.data && response.data.status ? response.data.status : ''
            )
            await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
          }
        } catch (error) {
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
        }
      }
    },
    [setFormData, locationGenerationProgress]
  )

  /**
   * Generate location map references
   */
  const generateLocationMap = useCallback(
    async (locationMapObj, existingUris) => {
      setIsGeneratingLocationReference(true)

      try {
        const locationRes = await generateLocationReference({
          location_map_with_prompts: locationMapObj,
          existing_location_uris:
            existingUris !== undefined
              ? existingUris
              : formData?.locationReference
        })
        const taskId =
          locationRes && locationRes.data && locationRes.data.task_id
        if (taskId) {
          await pollLocationTaskStatus(taskId)
        } else {
          setIsGeneratingLocationReference(false)
        }
      } catch (error) {
        console.error('Error generating location map:', error)
        setIsGeneratingLocationReference(false)
      }
    },
    [formData?.locationReference, pollLocationTaskStatus]
  )

  /**
   * Add new location reference
   */
  const handleAddLocationReference = useCallback(
    async newLocationMap => {
      setIsGeneratingLocationReference(true)

      try {
        const locationRes = await generateLocationReference({
          location_map_with_prompts: {
            ...(formData?.locationMap || {}),
            ...newLocationMap
          },
          existing_location_uris: formData?.locationReference
        })
        const taskId =
          locationRes && locationRes.data && locationRes.data.task_id
        if (taskId) {
          await pollAddLocationTaskStatus(taskId, newLocationMap)
        } else {
          setIsGeneratingLocationReference(false)
        }
      } catch (error) {
        console.error('Error adding location reference:', error)
        setIsGeneratingLocationReference(false)
      }
    },
    [
      formData?.locationMap,
      formData?.locationReference,
      pollAddLocationTaskStatus
    ]
  )

  /**
   * Edit existing location reference
   */
  const handleEditLocationReference = useCallback(
    async editingLocationPrompt => {
      setIsGeneratingLocationReference(true)

      try {
        const editingKeys = Object.keys(editingLocationPrompt)

        // Remove old URIs for edited locations
        const filteredLocationUris = Object.keys(
          formData?.locationReference || {}
        ).reduce((acc, key) => {
          if (!editingKeys.includes(key)) {
            acc[key] = formData.locationReference[key]
          }
          return acc
        }, {})

        const locationRes = await generateLocationReference({
          location_map_with_prompts: {
            ...(formData?.locationMap || {}),
            ...editingLocationPrompt
          },
          existing_location_uris: filteredLocationUris
        })

        const taskId = locationRes?.data?.task_id

        if (taskId) {
          await pollAddLocationTaskStatus(taskId, editingLocationPrompt)
        } else {
          setIsGeneratingLocationReference(false)
        }
      } catch (error) {
        console.error('Error editing location reference:', error)
        setIsGeneratingLocationReference(false)
      }
    },
    [
      formData?.locationMap,
      formData?.locationReference,
      pollAddLocationTaskStatus
    ]
  )

  /**
   * Batch update multiple locations
   */
  const handleBatchUpdateLocations = useCallback(
    async (updatedLocationMap, modifiedKeys) => {
      setIsGeneratingLocationReference(true)

      try {
        // Filter out URIs for modified keys
        const filteredLocationUris = Object.keys(
          formData?.locationReference || {}
        ).reduce((acc, key) => {
          if (!modifiedKeys.includes(key)) {
            acc[key] = formData.locationReference[key]
          }
          return acc
        }, {})

        const locationRes = await generateLocationReference({
          location_map_with_prompts: updatedLocationMap,
          existing_location_uris: filteredLocationUris
        })

        const taskId = locationRes?.data?.task_id

        if (taskId) {
          await pollAddLocationTaskStatus(taskId, updatedLocationMap)
        } else {
          setIsGeneratingLocationReference(false)
        }
      } catch (error) {
        console.error('Error batch updating location references:', error)
        setIsGeneratingLocationReference(false)
      }
    },
    [formData?.locationReference, pollAddLocationTaskStatus]
  )

  /**
   * Reset location generation state
   */
  const resetLocationGeneration = useCallback(() => {
    setIsGeneratingLocationReference(false)
    setLocationGenerationProgress(0)
    setLocationGenerationStatus('')
  }, [])

  return {
    // State
    isGeneratingLocationReference,
    locationGenerationProgress,
    locationGenerationStatus,

    // Actions
    generateLocationMap,
    handleAddLocationReference,
    handleEditLocationReference,
    handleBatchUpdateLocations,
    resetLocationGeneration
  }
}

export default useLocationReferences
